/**
 * CompanyDetailPage - Unified company profile view.
 * Shows ratings, analyst coverage, documents, price, topics.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  TrendingUp as UpgradeIcon,
  TrendingDown as DowngradeIcon,
  TrendingFlat as MaintainIcon,
  OpenInNew as OpenIcon,
  Person as PersonIcon,
  CompareArrows as CompareIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { getCompanyProfile, CompanyProfile, getCachedCompanyProfile } from '../services/v3RagService';

const CompanyDetailPage: React.FC = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ticker) return;
    // Use prefetch cache for instant render if available
    const cached = getCachedCompanyProfile(ticker);
    if (cached) {
      setProfile(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);
    getCompanyProfile(ticker)
      .then(data => setProfile(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [ticker]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Company not found'}</Alert>
      </Box>
    );
  }

  const { company, current_rating, rating_history, latest_price, analysts, documents, topics, valuation_methodology, estimates } = profile;

  const getRatingChangeColor = (change?: string | null) => {
    if (!change) return 'default';
    const c = change.toLowerCase();
    if (c === 'upgrade' || c === 'initiated') return 'success';
    if (c === 'downgrade') return 'error';
    return 'default';
  };

  const getRatingChangeIcon = (change?: string | null) => {
    if (!change) return <MaintainIcon fontSize="small" />;
    const c = change.toLowerCase();
    if (c === 'upgrade' || c === 'initiated') return <UpgradeIcon fontSize="small" />;
    if (c === 'downgrade') return <DowngradeIcon fontSize="small" />;
    return <MaintainIcon fontSize="small" />;
  };

  const upside = current_rating?.price_target && latest_price?.close_price
    ? ((current_rating.price_target / latest_price.close_price - 1) * 100).toFixed(1)
    : null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <BackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {company.name || company.ticker}
        </Typography>
        <Chip label={company.ticker} color="primary" size="medium" sx={{ fontWeight: 700 }} />
        {company.sector && (
          <Chip label={company.sector} variant="outlined" size="small" />
        )}
        {company.sub_sector && (
          <Chip label={company.sub_sector} variant="outlined" size="small" />
        )}
        {company.coverage_status && (
          <Chip
            label={company.coverage_status}
            color={company.coverage_status === 'active' ? 'success' : 'default'}
            size="small"
          />
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Rating Card */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('company.currentRating')}</Typography>
              {current_rating ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      {current_rating.rating || 'N/A'}
                    </Typography>
                    {current_rating.rating_change && (
                      <Chip
                        icon={getRatingChangeIcon(current_rating.rating_change)}
                        label={current_rating.rating_change}
                        color={getRatingChangeColor(current_rating.rating_change) as any}
                        size="small"
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {current_rating.price_target && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('company.priceTarget')}</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>${current_rating.price_target}</Typography>
                      </Box>
                    )}
                    {latest_price?.close_price && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('company.currentPrice')}</Typography>
                        <Typography variant="h5">
                          ${latest_price.close_price}
                          {latest_price.price_date && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              ({new Date(latest_price.price_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                    )}
                    {upside && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('company.upside')}</Typography>
                        <Typography
                          variant="h5"
                          sx={{ color: parseFloat(upside) >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}
                        >
                          {parseFloat(upside) >= 0 ? '+' : ''}{upside}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {current_rating.report_date && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {t('company.asOf')} {current_rating.report_date}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">{t('company.noRating')}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Covering Analysts */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('company.coveringAnalysts')}</Typography>
              {analysts.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {analysts.map((a, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <PersonIcon color="action" />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{a.name}</Typography>
                        {a.firm && (
                          <Typography variant="caption" color="text.secondary">{a.firm}</Typography>
                        )}
                        {a.sector_focus && (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({a.sector_focus})
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary">{t('company.noAnalysts')}</Typography>
              )}

              {/* Valuation */}
              {valuation_methodology && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom>{t('company.valuation')}</Typography>
                  <Typography variant="body2" color="text.secondary">{valuation_methodology}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Estimates */}
        {estimates && Object.keys(estimates).length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Estimates</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      {(() => {
                        const allPeriods = new Set<string>();
                        Object.values(estimates).forEach((metricData: any) => {
                          if (metricData && typeof metricData === 'object') {
                            Object.keys(metricData).forEach(p => allPeriods.add(p));
                          }
                        });
                        const sorted = Array.from(allPeriods).sort();
                        return sorted.map(p => <TableCell key={p} align="right">{p}</TableCell>);
                      })()}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(estimates).map(([metric, data]: [string, any]) => {
                      if (!data || typeof data !== 'object') return null;
                      const allPeriods = new Set<string>();
                      Object.values(estimates).forEach((md: any) => {
                        if (md && typeof md === 'object') Object.keys(md).forEach(p => allPeriods.add(p));
                      });
                      const sorted = Array.from(allPeriods).sort();
                      return (
                        <TableRow key={metric}>
                          <TableCell sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{metric}</TableCell>
                          {sorted.map(p => (
                            <TableCell key={p} align="right">
                              {data[p] != null ? (typeof data[p] === 'number' ? data[p].toLocaleString(undefined, { maximumFractionDigits: 2 }) : data[p]) : '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* Rating History */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>{t('company.ratingHistory')}</Typography>
            {rating_history.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('table.date')}</TableCell>
                      <TableCell>{t('table.rating')}</TableCell>
                      <TableCell>{t('table.target')}</TableCell>
                      <TableCell>{t('table.change')}</TableCell>
                      <TableCell>{t('company.prevRating')}</TableCell>
                      <TableCell>{t('company.prevTarget')}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rating_history.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.report_date || '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{r.rating || '-'}</TableCell>
                        <TableCell>{r.price_target ? `$${r.price_target}` : '-'}</TableCell>
                        <TableCell>
                          {r.rating_change ? (
                            <Chip
                              label={r.rating_change}
                              color={getRatingChangeColor(r.rating_change) as any}
                              size="small"
                              variant="outlined"
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell>{r.prev_rating || '-'}</TableCell>
                        <TableCell>{r.prev_target ? `$${r.prev_target}` : '-'}</TableCell>
                        <TableCell>
                          {i < rating_history.length - 1 && (r as any).document_id && (rating_history[i + 1] as any).document_id && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/compare?left=${(r as any).document_id}&right=${(rating_history[i + 1] as any).document_id}`, '_blank');
                              }}
                              title="Compare with previous"
                            >
                              <CompareIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No rating history available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Recent Documents */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>{t('company.recentDocuments')}</Typography>
            {documents.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('table.title')}</TableCell>
                      <TableCell>{t('table.date')}</TableCell>
                      <TableCell>{t('table.type')}</TableCell>
                      <TableCell>{t('table.analyst')}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow
                        key={doc.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/document-pro?id=${doc.id}`)}
                      >
                        <TableCell sx={{ maxWidth: 400 }}>
                          <Typography variant="body2" noWrap title={doc.title || ''}>
                            {doc.title || 'Untitled'}
                          </Typography>
                        </TableCell>
                        <TableCell>{doc.report_date || '-'}</TableCell>
                        <TableCell>{doc.report_type || '-'}</TableCell>
                        <TableCell>
                          {doc.analyst_names?.join(', ') || '-'}
                        </TableCell>
                        <TableCell>
                          <OpenIcon fontSize="small" color="action" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No documents available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Topics */}
        {topics.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>{t('company.topics')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {topics.map((topic, i) => (
                  <Chip key={i} label={topic} variant="outlined" size="small" />
                ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CompanyDetailPage;
